import apiClient from '@/lib/api/apiClient'
import type { ExtendedAxiosRequestConfig } from '@/lib/api/types'
import type { ApiError } from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { extractErrorMessage } from '@/lib/utils/errorHandling'

/** Public forgot/reset password API calls (unauthenticated). */

const SKIP_AUTH = { skipAuth: true } as ExtendedAxiosRequestConfig

export async function requestPasswordReset(email: string): Promise<string> {
  try {
    const response = await apiClient.post<unknown>(
      API_ENDPOINTS.FORGOT_PASSWORD,
      { email },
      SKIP_AUTH,
    )
    return extractSuccessMessage(response.data, 'Check your email for further instructions.')
  } catch (error) {
    const apiError = error as ApiError
    throw {
      message: extractErrorMessage(error, 'Failed to send reset email. Please try again.'),
      status: apiError.status ?? 400,
    } as ApiError
  }
}

export async function verifyResetToken(token: string): Promise<void> {
  try {
    await apiClient.get(API_ENDPOINTS.RESET_PASSWORD_VERIFY(token), SKIP_AUTH)
  } catch (error) {
    const apiError = error as ApiError
    throw {
      message: extractErrorMessage(error, 'This reset link is invalid or has expired.'),
      status: apiError.status ?? 400,
    } as ApiError
  }
}

export async function submitNewPassword(
  token: string,
  newPassword: string,
): Promise<string> {
  try {
    const response = await apiClient.post<unknown>(
      API_ENDPOINTS.RESET_PASSWORD,
      { code_password: token, new_password: newPassword },
      SKIP_AUTH,
    )
    return extractSuccessMessage(response.data, 'Password Change Successfully')
  } catch (error) {
    const apiError = error as ApiError
    throw {
      message: extractErrorMessage(error, 'Failed to reset password. Please try again.'),
      status: apiError.status ?? 400,
    } as ApiError
  }
}

function extractSuccessMessage(payload: unknown, fallback: string): string {
  if (typeof payload === 'string' && payload.trim()) return payload
  if (payload && typeof payload === 'object') {
    const msg = (payload as { message?: unknown }).message
    if (typeof msg === 'string' && msg.trim()) return msg
  }
  return fallback
}
